// @flow

import * as React from 'react';
import {useContext, useState, useRef} from 'react';
import {contextStubFunction} from 'docs/util';

export type ContentFrameContextType = {|
  getScrollPosition: () => number,
  setScrollPosition: (position: number, animate?: boolean) => void,
  pinAt: (element: HTMLElement) => void,
  setContainerRef: (ref: React.ElementRef<any>) => void,
  onScroll: ((SyntheticEvent<HTMLElement>) => void) => any,
  scrollToStep: (position: number) => void,
|};

const ContentFrameContext = React.createContext<ContentFrameContextType>({
  getScrollPosition: contextStubFunction('Scrollable'),
  setScrollPosition: contextStubFunction('Scrollable'),
  onScroll: contextStubFunction('Scrollable'),
  pinAt: contextStubFunction('Scrollable'),
  setContainerRef: contextStubFunction('Scrollable'),
  scrollToStep: contextStubFunction('Scrollable'),
});

type ContentFrameProviderProps = {|
  children: React.Node,
|};

export const ContentFrameProvider = ({children}: ContentFrameProviderProps) => {
  const [containerRef, setContainerRef] = useState(null);
  // Keep track of whether or not we're in the middle of an animated scroll.
  // We use a ref here so we can update it without re-rendering.
  const isAnimatingScroll = useRef(false);

  const getContainerElement = (): ?HTMLElement => {
    if (!containerRef?.current) {
      return null;
    }
    return containerRef.current;
  };

  const getScrollPosition = () => {
    const contentEle = getContainerElement();
    if (!contentEle) {
      return 0;
    }
    return contentEle.scrollTop;
  };

  const getIsAtScrollPosition = (position: number): boolean => {
    const contentEle = getContainerElement();
    if (!contentEle) {
      return false;
    }
    return contentEle.scrollTop === position;
  };

  const getIsAtBottom = (): boolean => {
    const contentEle = getContainerElement();
    if (!contentEle) {
      return false;
    }
    return (
      contentEle.scrollHeight - contentEle.scrollTop ===
      contentEle.getBoundingClientRect().height
    );
  };

  // Set the scroll y position on the container and optionally animate the
  // scroll. If we animate the scroll we disable scroll event handling until
  // the animated scrolling is complete.
  const setScrollPosition = (position: number, animate?: boolean = false) => {
    const contentEle = getContainerElement();

    if (!contentEle) {
      return;
    }
    if (isAnimatingScroll.current) {
      return;
    }
    // If scroll would be a no-op, just return
    if (
      getIsAtScrollPosition(position) ||
      (getIsAtBottom() && position > contentEle.scrollTop)
    ) {
      return;
    }
    // Simple case: no scroll animation, just set the y position and return.
    if (!animate) {
      contentEle.scrollTo(0, position);
      return;
    }
    // Complex case: animated scroll. we add a new scroll handler to
    // watch for our animated scroll to be completed, then we unset our
    // isAnimatingScroll bool. This enables our onScroll handler to be
    // disabled when an animated scroll is being conducted (and thus we
    // avoid unintentional scroll event handling during animation).
    const watchForScrollCompletion = () => {
      if (getIsAtScrollPosition(position) || getIsAtBottom()) {
        contentEle.removeEventListener('scroll', watchForScrollCompletion);
        // Skip a beat so we make sure all scroll events have fired before
        // setting our bool to false.
        requestAnimationFrame(() => {
          isAnimatingScroll.current = false;
        });
      }
    };
    isAnimatingScroll.current = true;
    contentEle.addEventListener('scroll', watchForScrollCompletion);
    contentEle.scrollTo({
      top: position,
      left: 0,
      behavior: 'smooth',
    });
  };

  const onScroll = (handler) => {
    if (!containerRef?.current) {
      return;
    }
    const wrappedHandler = (e) => {
      // Swallow scroll events while we are scroll animating so that we don't
      // trigger scroll-based UI updates between the current position and
      // destination position.
      if (isAnimatingScroll.current) {
        return;
      }
      handler(e);
    };
    containerRef.current.addEventListener('scroll', wrappedHandler);
    return () => {
      if (!containerRef?.current) {
        return;
      }
      containerRef.current.removeEventListener('scroll', wrappedHandler);
    };
  };

  // We often synchronize tab groups; for example, CodeTabGroups are controlled by
  // the value of a pref. Because tabs might contain content of different sizes,
  // when the user selects a new tab, content that lies above the element the user
  // is interacting with might change size. This can cause content to "jump", which
  // can be confusing for users.
  //
  // To avoid this problem, this function allows the browser to reflow, and then
  // immediately adjusts the scroll position of the content frame to keep the content
  // the user is interacting with in the same position.
  const pinAt = (element: HTMLElement) => {
    const oldOffset = element.getBoundingClientRect().top;
    requestAnimationFrame(() => {
      const position = getScrollPosition();
      const newOffset = element.getBoundingClientRect().top;
      setScrollPosition(position + (newOffset - oldOffset));
    });
  };

  // Used to scroll in the Integration builder
  const scrollToStep = (position: number) => {
    const contentEle = getContainerElement();

    if (!contentEle) {
      return;
    }

    contentEle.scrollTo({
      top: position,
      left: 0,
      behavior: 'smooth',
    });
  };

  const context = {
    getScrollPosition,
    setScrollPosition,
    pinAt,
    setContainerRef,
    onScroll,
    scrollToStep,
  };

  return (
    <ContentFrameContext.Provider value={context}>
      {children}
    </ContentFrameContext.Provider>
  );
};

export const useContentFrame = () => {
  return useContext(ContentFrameContext);
};
